// アプリ（じゆうにつくる）の通報ダイアログ。ガワはさくじょのダイアログ（DeleteMessageBox）と同じ作り、
// 中身（絵のカード・入力欄）はピースビルダーの通報（BuilderReportDialog）と同じ作りにする。
// 理由が無いと運営が判断できないので、絵で理由を選ぶ＋自由に書く。理由を選ぶまで送れない。
import { useState, useEffect } from "react";
import { Dialog } from "@mui/material";
import I18n from "common/i18n";
import Constants from "common/constant";
import { useScale, usePlaySound } from "common/utils";
import { CloseBtn, FadeTransition } from "common/elements";

/** api の通報理由と同じ値 */
enum ReportReason {
  SPAM = "spam",
  OTHER = "other",
  COPYRIGHT = "copyright",
  INAPPROPRIATE = "inappropriate",
  DISLIKE_EXPRESSION = "dislike_expression",
}

// 絵はピースビルダーの通報と共通。正方形なので切らずに収め、余白を絵の背景色で埋める
const REASONS: { reason: ReportReason; image: string; bg: string; labelKey: string }[] = [
  {
    reason: ReportReason.INAPPROPRIATE,
    image: "report_inappropriate",
    bg: "bg-[#f5c6cd]",
    labelKey: "MSG_BUILDER_REPORT_REASON_INAPPROPRIATE",
  },
  {
    reason: ReportReason.DISLIKE_EXPRESSION,
    image: "report_dislike",
    bg: "bg-[#bde2ee]",
    labelKey: "MSG_BUILDER_REPORT_REASON_DISLIKE",
  },
  {
    reason: ReportReason.SPAM,
    image: "report_spam",
    bg: "bg-[#faed6f]",
    labelKey: "MSG_BUILDER_REPORT_REASON_SPAM",
  },
  {
    reason: ReportReason.COPYRIGHT,
    image: "report_copy",
    bg: "bg-[#cde8c8]",
    labelKey: "MSG_BUILDER_REPORT_REASON_COPYRIGHT",
  },
  {
    reason: ReportReason.OTHER,
    image: "report_other",
    bg: "bg-[#dbccee]",
    labelKey: "MSG_BUILDER_REPORT_REASON_OTHER",
  },
];

// 172px のカード 5 枚が 1 行に入る幅。高さは中身ぴったり（下に余白を残さない）
const WIDTH = 940;
const HEIGHT = 624;

export const ReportProjectDialogMessage = ({
  name,
  visible,
  report,
  close,
}: {
  name: string;
  visible: boolean;
  report: (reason: string, detail: string) => void;
  close: () => void;
}) => {
  const play = usePlaySound();
  const { scale } = useScale();
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [detail, setDetail] = useState("");
  const [disable, setDisable] = useState(false);

  // 開くたびに選び直す。前の通報の内容が残っていると誤送信になる
  useEffect(() => {
    if (visible) {
      setReason(null);
      setDetail("");
      setDisable(false);
    }
  }, [visible]);

  const handleSelect = (next: ReportReason) => () => {
    play();
    setReason(next);
  };

  const handleReport = () => {
    if (disable || !reason) return;
    setDisable(true);
    report(reason, detail.trim());
  };

  const quoted = name?.length < 16 ? name : `${name?.slice(0, 16)}...`;

  return (
    <Dialog
      maxWidth={false}
      open={visible}
      sx={{ zIndex: 99999 }}
      TransitionComponent={FadeTransition}
      componentsProps={{
        backdrop: { style: { backgroundColor: "transparent" } },
      }}
      PaperProps={{
        style: {
          margin: 0,
          overflow: "visible",
          backgroundColor: "transparent",
        },
        elevation: 0,
      }}
    >
      <div
        className="flex-col-el flex-center relative"
        style={{
          filter:
            "drop-shadow(0px 6px 6px rgb(0 0 0 / 20%)) drop-shadow(0px 10px 14px rgb(0 0 0 / 14%)) drop-shadow(0px 4px 18px rgb(0 0 0 / 12%))",
        }}
      >
        <div
          className="flex-col-el flex-center relative"
          style={{ width: WIDTH * scale, height: HEIGHT * scale }}
        >
          <div
            className="flex-col-el flex-center relative"
            style={{ transform: `scale(${scale})` }}
          >
            <div className="flex-col-el justify-start w-[940px] h-[624px] relative border-solid border-white border-[8px] rounded-lg bg-white">
              <CloseBtn close={close} />

              <div className="flex-col-el justify-start w-full h-full relative p-4 gap-4 box-border rounded bg-beige-60">
                <div className="flex-row-el items-center justify-start gap-2 pt-4">
                  <span className="material-symbols-outlined type-title text-red-100">flag</span>
                  <p className="type-text-left type-title text-textcolor-black">
                    {I18n.t("MSG_BUILDER_MENU_REPORT")}
                  </p>
                </div>

                <div className="flex-col-el items-start w-full">
                  {I18n.t("MSG_BUILDER_REPORT_PIECE_CONFIRM")
                    .split("\n")
                    .map((line, index) => (
                      <p key={index} className="type-text-left type-body text-textcolor-black">
                        {index === 0 ? `「${quoted}」${line}` : line}
                      </p>
                    ))}
                </div>

                <div className="flex-col-el items-start w-full gap-2">
                  <p className="type-text-left type-body text-textcolor-black">
                    {I18n.t("MSG_BUILDER_REPORT_REASON_TITLE")}
                  </p>

                  <div className="flex-row-el items-stretch justify-between w-full">
                    {REASONS.map((item) => {
                      const selected = reason === item.reason;
                      return (
                        <button
                          key={item.reason}
                          type="button"
                          onClick={handleSelect(item.reason)}
                          className="type-inherit-font flex-col-el flex-center relative w-[172px] p-0 border-none bg-white rounded-xl overflow-hidden cursor-pointer"
                        >
                          <img
                            alt=""
                            src={`${Constants.assetHost}/assets/images/${item.image}.png`}
                            className={`w-full h-[112px] object-contain ${item.bg}`}
                          />
                          <div
                            className={`flex-col-center w-full h-[44px] px-1 box-border transition-colors ${
                              selected ? "bg-red-100" : "bg-blue-80"
                            }`}
                          >
                            <p className="type-text type-sm text-white whitespace-nowrap">
                              {I18n.t(item.labelKey)}
                            </p>
                          </div>
                          <div
                            className={`flex-col-el absolute w-full h-full rounded-xl box-border ${
                              selected ? "border-solid border-4 border-red-100" : "border-none"
                            }`}
                          />
                        </button>
                      );
                    })}
                  </div>
                </div>

                <textarea
                  rows={2}
                  value={detail}
                  maxLength={500}
                  onChange={(e) => setDetail(e.target.value)}
                  placeholder={I18n.t("MSG_BUILDER_REPORT_DETAIL_LABEL")}
                  className="type-text-left type-body text-textcolor-black w-full m-0 box-border rounded-xl p-3 border-solid border-2 border-gray2-30 outline-none resize-none bg-white focus:border-blue-80"
                />

                {/* ボタンはさくじょのダイアログ（DeleteMessageBox）と同じ昔の btn の作り。けっていなので緑。
                    btn の余白は em で決まるので type-inherit-font は付けない（付けると大きくなる）。
                    アイコンは 24px の中の type-title で「さくじょする」と同じ 36px にする。
                    type-title の行の高さのままだと背が高くなるので、「さくじょする」の実測
                    （アイコン 40px の行 + 下 3px = 43px）に合わせて 39px + mb-1（4px）にする */}
                <div className="flex-row-el flex-center w-full alpha">
                  <button
                    type="button"
                    disabled={!reason}
                    onClick={handleReport}
                    className="btn btn-primary"
                  >
                    <div className="flex-row-center gap-1 mb-1 text-font-size-body1">
                      <span className="material-symbols-outlined type-title !leading-[39px] text-white">
                        flag
                      </span>
                      <p className="text-body text-white text-font-size-body1">
                        {I18n.t("MSG_CONFIRM_BTN")}
                      </p>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
